import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { boxAPI } from '../utils/apiClient';
import SlotMachine from '../components/SlotMachine';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/BoxDetailsPage.css';

const BoxDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUserBalance } = useAuth();
  
  // Prefer box passed from navigation state to avoid refetch when API is down
  const stateBox = location.state && location.state.box ? location.state.box : null;
  const [box, setBox] = useState(stateBox);
  const [loading, setLoading] = useState(!stateBox);
  const [purchasing, setPurchasing] = useState(false);
  const [opening, setOpening] = useState(false);
  const [showSlotMachine, setShowSlotMachine] = useState(false);
  const [wonItem, setWonItem] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!box || String(box.id) !== String(id)) {
      fetchBox();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBox = async () => {
    try {
      const response = await boxAPI.getById(id);
      // Support both { box: {...} } and direct object responses
      setBox(response.data?.box ?? response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching box:', error);
      if (stateBox) {
        // Keep the state box if provided
        setBox(stateBox);
        setError('');
      } else {
        setError('Box not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseAndOpen = async () => {
    if (!box) return;

    if ((user?.balance ?? 0) < box.price) {
      setError('Insufficient balance. Please add funds to your wallet.');
      return;
    }

    setPurchasing(true);
    setError('');

    try {
      // MVP: Skip separate purchase call; open performs the action and returns new balance
      setOpening(true);
      const openResponse = await boxAPI.open(id);

      const newBalance = openResponse.data?.newBalance;
      if (typeof newBalance === 'number') {
        updateUserBalance(newBalance);
      } else {
        // Fallback: deduct locally
        updateUserBalance(Math.max(0, (user?.balance ?? 0) - Number(box.price || 0)));
      }
      setWonItem(openResponse.data?.item || null);
      setShowSlotMachine(true);
      
    } catch (error) {
      console.error('Error purchasing/opening box:', error);
      setError(error.response?.data?.error || 'Failed to open box');
    } finally {
      setPurchasing(false);
    }
  };

  const handleSlotMachineComplete = () => {
    setShowSlotMachine(false);
    setOpening(false);
    navigate('/inventory');
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: '#8B9DC3',
      uncommon: '#5CB85C',
      rare: '#5BC0DE',
      epic: '#9B59B6',
      legendary: '#F39C12'
    };
    return colors[rarity] || colors.common;
  };

  if (loading) {
    return <LoadingSpinner message="Loading mystery box..." />;
  }

  if (error && !box) {
    return (
      <div className="error-container">
        <h2>Mystery Box Not Found</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="back-btn">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="box-details-page">
      <div className="container">
        <button onClick={() => navigate(-1)} className="back-btn">
          ← Back
        </button>

        <div className="box-details-container">
          <div className="box-showcase">
            <div className="box-image-large">
              {box.imageUrl ? (
                <img src={box.imageUrl} alt={box.name} />
              ) : (
                <div className="box-placeholder-large">
                  <span className="box-emoji">🎁</span>
                </div>
              )}
              <div className="box-category-badge-large">{box.category}</div>
            </div>

            <div className="box-info-panel">
              <h1 className="box-title">{box.name}</h1>
              <p className="box-description">{box.description}</p>
              
              <div className="box-price-section">
                <div className="price-display">
                  <span className="price-label">Price:</span>
                  <span className="price-value">${parseFloat(box.price).toFixed(2)}</span>
                </div>
                
                <div className="balance-display">
                  <span className="balance-label">Your Balance:</span>
                  <span className={`balance-value ${(user?.balance ?? 0) < box.price ? 'insufficient' : ''}`}>
                    ${parseFloat(user?.balance ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <div className="action-buttons">
                <button
                  onClick={handlePurchaseAndOpen}
                  disabled={purchasing || opening || (user?.balance ?? 0) < box.price}
                  className={`open-box-btn ${purchasing || opening ? 'loading' : ''}`}
                >
                  {purchasing ? (
                    'Purchasing...'
                  ) : opening ? (
                    'Opening...'
                  ) : (user?.balance ?? 0) < box.price ? (
                    'Insufficient Balance'
                  ) : (
                    `Open Box - $${parseFloat(box.price).toFixed(2)}`
                  )}
                </button>

                {(user?.balance ?? 0) < box.price && (
                  <button 
                    onClick={() => navigate('/wallet')}
                    className="add-funds-btn"
                  >
                    Add Funds
                  </button>
                )}
              </div>

              <div className="box-stats">
                <div className="stat">
                  <span className="stat-label">Total Opened:</span>
                  <span className="stat-value">{box.openedBoxes || 0}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Possible Items:</span>
                  <span className="stat-value">{Array.isArray(box.items) ? box.items.length : 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="possible-items-section">
            <h2>Possible Items</h2>
            <div className="items-grid">
              {Array.isArray(box.items) && box.items.length > 0 ? (
                box.items.map((item, idx) => (
                  <div key={item?.id ?? idx} className="item-card">
                    <div 
                      className="item-border"
                      style={{ borderColor: getRarityColor(item?.rarity || 'common') }}
                    >
                      {item?.imageUrl ? (
                        <img src={item.imageUrl} alt={item?.name || 'Item'} className="item-image" />
                      ) : (
                        <div className="item-placeholder">
                          <span className="item-emoji">💎</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="item-info">
                      <h3 className="item-name">{item?.name || 'Mystery Item'}</h3>
                      <span 
                        className="item-rarity"
                        style={{ color: getRarityColor(item?.rarity || 'common') }}
                      >
                        {(item?.rarity || 'common').toUpperCase()}
                      </span>
                      <div className="item-value">${parseFloat(item?.value ?? 0).toFixed(2)}</div>
                      <div className="drop-rate">
                        {((parseFloat(item?.BoxItem?.dropRate ?? 0) * 100)).toFixed(1)}% chance
                      </div>
                      {item?.description && (
                        <p className="item-description">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-items">No item details available for this box yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSlotMachine && (
        <SlotMachine
          items={wonItem ? [wonItem] : []}
          onComplete={handleSlotMachineComplete}
          isVisible={showSlotMachine}
        />
      )}
    </div>
  );
};

export default BoxDetailsPage;
